import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Palette, Users, Package, PaintBucket, Workflow } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: <Palette className="h-12 w-12 text-emerald-400" />,
      title: "Fashion Designer",
      description: "Creating original clothing, accessories, and footwear, blending creativity with technical skills.",
      features: [
        "Custom couture and ready-to-wear",
        "Trend forecasting",
        "Material selection",
        "Collection development"
      ]
    },
    {
      icon: <PaintBucket className="h-12 w-12 text-emerald-400" />,
      title: "Textile Designer",
      description: "Designing innovative fabrics and patterns for fashion and interiors.",
      features: [
        "Print and pattern creation",
        "Fabric innovation",
        "Surface embellishment",
        "Sustainable textiles"
      ]
    },
    {
      icon: <Palette className="h-12 w-12 text-emerald-400" />,
      title: "Fashion Illustrator",
      description: "Bringing fashion concepts to life through detailed sketches and digital illustrations.",
      features: [
        "Hand-drawn and digital sketches",
        "Concept art",
        "Lookbook illustration",
        "Technical flats"
      ]
    },
    {
      icon: <Package className="h-12 w-12 text-emerald-400" />,
      title: "Garment Technologist",
      description: "Ensuring the perfect fit, quality, and construction of every garment.",
      features: [
        "Pattern testing",
        "Fabric performance analysis",
        "Quality control",
        "Fit sessions"
      ]
    },
    {
      icon: <Workflow className="h-12 w-12 text-emerald-400" />,
      title: "Patternmaker",
      description: "Translating designs into precise patterns for flawless garment production.",
      features: [
        "Pattern drafting",
        "Grading and sizing",
        "Sample making",
        "Pattern digitization"
      ]
    },
    {
      icon: <Users className="h-12 w-12 text-emerald-400" />,
      title: "Costume Designer",
      description: "Designing costumes for film, theater, and special events with creative flair.",
      features: [
        "Historical and contemporary costumes",
        "Character research",
        "Wardrobe management",
        "On-set fittings"
      ]
    },
    {
      icon: <Camera className="h-12 w-12 text-emerald-400" />,
      title: "Fashion Photographer",
      description: "Capturing the essence of style through editorial and commercial photography.",
      features: [
        "Editorial shoots",
        "Lookbook photography",
        "Studio and outdoor sessions",
        "Post-production editing"
      ]
    },
    {
      icon: <Users className="h-12 w-12 text-emerald-400" />,
      title: "Fashion Stylist",
      description: "Curating looks and styling for shoots, shows, and personal clients.",
      features: [
        "Wardrobe selection",
        "On-set styling",
        "Personal shopping",
        "Trend consulting"
      ]
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1581655353564-df123a1eb820?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <motion.div 
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          <motion.h1 
            variants={fadeInUp}
            className="text-5xl md:text-7xl font-bold mb-6"
          >
            <span className="block">Fashion Design</span>
            <span className="block">Services</span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            From bespoke couture to sustainable collections, discover our comprehensive range of fashion design expertise.
          </motion.p>
        </motion.div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
            {services.map((service, index) => (
              <div key={index} className="p-8 border border-gray-800 rounded-lg hover:border-emerald-400 transition-colors">
                <div className="mb-6">{service.icon}</div>
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-400 mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-300">
                      <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full mr-2"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-white">Our Process</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A refined, collaborative approach to delivering exceptional results.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-400 text-emerald-400 text-3xl font-bold shadow-lg">1</span>,
                title: "Consultation",
                description: "In-depth discussion to understand your vision and requirements."
              },
              {
                icon: <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-400 text-emerald-400 text-3xl font-bold shadow-lg">2</span>,
                title: "Concept Development",
                description: "Creating detailed plans and mood boards for your approval."
              },
              {
                icon: <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 border-2 border-emerald-400 text-emerald-400 text-3xl font-bold shadow-lg">3</span>,
                title: "Execution & Refinement",
                description: "Bringing your vision to life with meticulous attention to detail."
              }
            ].map((phase, index) => (
              <div key={index} className="relative z-10 flex flex-col items-center text-center bg-gray-900 rounded-2xl shadow-xl p-10 transition-transform hover:-translate-y-2 hover:shadow-emerald-400/30">
                {phase.icon}
                <h3 className="text-xl font-bold mt-6 mb-2 text-white">{phase.title}</h3>
                <p className="text-gray-400 mb-2">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Let's Create Something Exceptional</h2>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Ready to elevate your visual narrative? Contact us to discuss your project and discover how we can bring your vision to life.
          </p>
          <button className="px-8 py-3 bg-emerald-400 text-black font-semibold rounded-full hover:bg-emerald-300 transition-colors">
            Book a Consultation
          </button>
        </div>
      </section>
    </motion.div>
  );
};

export default ServicesPage;